import Button from '@pelckmans/elementis/components/button';
import ButtonGroup from '@pelckmans/elementis/components/button-group';
import { useToast } from '@pelckmans/elementis/components/toast';
import CheckIcon from '@pelckmans/elementis/icons/check';
import CloseIcon from '@pelckmans/elementis/icons/close';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import LocalStorageKeys from '../../../../constants/LocalStorageKeys';
import { TOAST_ID } from '../../../../constants/ToastIds';
import IdentityContext from '../../../../contexts/IdentityContext';
import { buildGroupProps } from '../../../../utils/elementisUtils';
import LocalStorage from '../../../../utils/LocalStorage';
import { acceptLicenseTransfer, declineLicenseTransfer } from '../../api/licenses';

const BASE = 'PAGES.PROFILE.LICENSES.MY_LICENSES.TRANSFER';

export default function LicenseTransferActions({
  transferId,
  onDecline,
  onAccept,
  refetchLicenseTransfers,
}: {
  transferId: string;
  onDecline: () => void;
  onAccept: () => void;
  refetchLicenseTransfers: () => void;
}) {
  const { t } = useTranslation();
  const { refreshIdentity } = useContext(IdentityContext);
  const { addToast } = useToast();

  const handleDecline = async () => {
    try {
      await declineLicenseTransfer(transferId);
      addToast({
        id: TOAST_ID.LICENSE_TRANSFER_DECLINED,
        variant: 'success',
        children: t(`${BASE}.TOASTS.DECLINE_TRANSFER.SUCCESS`),
      });

      onDecline();
    } catch (error) {
      addToast({
        id: TOAST_ID.LICENSE_TRANSFER_DECLINED,
        variant: 'danger',
        children: t(`ALERTS.RETRY.MESSAGE`),
        actions: buildGroupProps(
          <Button prominence="danger" onClick={handleDecline} key="retry">
            {t(`ALERTS.RETRY.ACTION`)}
          </Button>,
        ),
      });
      refetchLicenseTransfers();
    }
  };

  const handleAccept = async () => {
    try {
      const { needsRefreshToken } = await acceptLicenseTransfer(transferId);
      if (needsRefreshToken) {
        const refreshToken = LocalStorage.get(LocalStorageKeys.p_refresh_token);
        if (refreshToken) {
          refreshIdentity(JSON.parse(refreshToken));
        }
      }

      addToast({
        id: TOAST_ID.LICENSE_TRANSFER_ACCEPTED,
        variant: 'success',
        children: t(`${BASE}.TOASTS.ACCEPT_TRANSFER.SUCCESS`),
      });

      onAccept();
    } catch (error) {
      addToast({
        id: TOAST_ID.LICENSE_TRANSFER_ACCEPTED,
        variant: 'danger',
        children: t(`ALERTS.RETRY.MESSAGE`),
        actions: buildGroupProps(
          <Button prominence="danger" onClick={handleAccept} key="retry">
            {t(`ALERTS.RETRY.ACTION`)}
          </Button>,
        ),
      });
      refetchLicenseTransfers();
    }
  };

  return (
    <ButtonGroup
      {...buildGroupProps(
        <>
          <Button size="sm" prominence="danger" startIcon={<CloseIcon />} onClick={handleDecline} key="decline">
            {t('ACTIONS.DECLINE')}
          </Button>
          <Button size="sm" prominence="success" startIcon={<CheckIcon />} onClick={handleAccept} key="accept">
            {t('ACTIONS.ACCEPT')}
          </Button>
        </>,
      )}
      size="sm"
    />
  );
}
